import { FormType, apiService } from 'app-core'
import { ErrorObject, Validation, ValidationArgs } from '@vuelidate/core'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import i18n from '@/i18n'

interface FormErrors {
  [key: string]: string[];
}

interface IRule {
  type: string;
  min: number;
  max: number;
}

export default class Form<T extends {}> implements FormType<T> {
  $busy: boolean
  $errors: FormErrors
  initialData: T
  data: T

  constructor(data: T) {
    this.$busy = false
    this.$errors = {}
    this.data = data
    this.initialData = { ...data }
  }

  setData(data: T) {
    for (const key in this.data) {
      this.data[key] = data[key]
    }
  }

  resetData() {
    for (const key in this.initialData) {
      this.data[key] = this.initialData[key]
    }
  }

  /**
   * Start processing the form
   */
  startProcessing() {
    this.$busy = true
    this.$errors = {}
  }

  /**
   * Finish processing the form
   */
  finishProcessing() {
    this.$busy = false
  }

  /**
   * Reset the errors and other state for the form
   */
  reset() {
    this.$busy = false
    this.$errors = {}
  }

  /**
   * Validate the whole form or the specific field
   */
  validate(validation: Validation<ValidationArgs, T>, field?: string) {
    if(!field) {
      const errors: FormErrors = {}

      // get the form properties that have errors as an array of unique items
      const uniqueItems = [
        ...new Set(validation.$silentErrors.map((item: ErrorObject) => {
          if(item.$validator === '$each') {
            const property = item.$property
            const errors = item.$response.$errors

            return errors.map((errorObj: any, index: number) => {
              return Object.keys(errorObj).filter(key => errorObj[key].length).map(key => `${property}.${index}.${key}`)
            }).flat()
          } else {
            return item.$property
          }
        }))
      ]

      uniqueItems.flat().forEach(element => {
        errors[element] = this.getFieldErrors(validation, element)
      })

      // assign this object to this.$errors
      this.$errors = errors
      return this.isValid()
    }

    this.$errors = { ...this.$errors, [field]: this.getFieldErrors(validation, field) }
    return this.isValid(field)
  }

  /**
   * Validate and return errors for the specific field
   */
  getFieldErrors(validation: Validation<ValidationArgs, T>, field: string) {

    const errors: string[] = []
    const deep = /\.\d+\./.test(field)

    if(!deep && !validation[field]) return errors
    const rules: IRule[] = []

    if(!deep) {
      Object.keys(validation[field]).forEach((key) => {
        if(!key.startsWith("$") && validation[field][key].$invalid == true) {
          rules.push(validation[field][key].$params)
        }
      })

      validation[field].$touch()
    } else {
      const [parentField, index, childField] = field.split('.')
      validation[parentField]?.$silentErrors.forEach((se: any) => {
        se.$response.$errors[index][childField]?.forEach((field: any) => {
          rules.push(field.$params)
        })
      })

      validation[parentField].$touch()
    }

    rules.filter(r => !!r).forEach(rule => {
      const error = Form.errorMessage(rule)
      errors.push(error)
    })

    return errors
  }

  /**
   * Check if the whole form or the specific field is valid
   */
  isValid(field: string | null = null) {
    if(field) {
      return !this.$errors[field] || !this.$errors[field].length
    }

    let valid = true

    Object.keys(this.$errors).forEach((field) => {
      if(this.$errors[field] && this.$errors[field].length) {
        valid = false
      }
    })

    return valid
  }

  /**
   * Set the errors on the form
   */
  setErrors(errors: FormErrors) {
    this.$errors = errors || {}
  }

  /**
   * Clear the errors on the form or a single field
   */
  clearErrors(field?: string) {
    if(!field) {
      this.$errors = {}
      return;
    }

    delete this.$errors[field];
  }

  /**
   * Helper method for making POST HTTP requests.
   */
  post(uri: string, config?: AxiosRequestConfig) {
    return this.send('post', uri, config)
  }

  /**
   * Helper method for making PUT HTTP requests.
   */
  put(uri: string, config?: AxiosRequestConfig) {
    return this.send('put', uri, config)
  }

  /**
   * Helper method for making PATCH HTTP requests.
   */
  patch(uri: string, config?: AxiosRequestConfig) {
    return this.send('patch', uri, config)
  }

  /**
   * Send the form to the back-end server.
   */
  async send<K>(method: 'post' | 'patch' | 'put', uri: string, config?: AxiosRequestConfig): Promise<AxiosResponse<K>> {
    this.startProcessing()

    try {
      const response = await apiService[method]<K>(uri, this.data, config as any)
      this.finishProcessing()
      return response as AxiosResponse<K>;
    } catch (error: any) {
      this.finishProcessing()
      this.setErrors(error.response.data.errors)
      throw error;
    }
  }

  /**
   * Get validation messages
   */
  static errorMessage(rule: IRule): string {
    switch(rule.type) {
      case 'required':
        return i18n.global.t('validation.required')
      case 'requiredIf':
        return i18n.global.t('validation.required')
      case 'requiredUnless':
        return i18n.global.t('validation.required')
      case 'minLength':
        return i18n.global.t('validation.min_length', { value: rule.min })
      case 'maxLength':
        return i18n.global.t('validation.max_length', { value: rule.max })
      case 'minValue':
        return i18n.global.t('validation.min_value', { value: rule.min })
      case 'maxValue':
        return i18n.global.t('validation.max_value', { value: rule.max })
      case 'between':
        return i18n.global.t('validation.between', { min: rule.min, max: rule.max })
      case 'alpha':
        return i18n.global.t('validation.alpha')
      case 'alphaNum':
        return i18n.global.t('validation.alpha_num')
      case 'numeric':
        return i18n.global.t('validation.numeric')
      case 'integer':
        return i18n.global.t('validation.integer')
      case 'decimal':
        return i18n.global.t('validation.decimal')
      case 'email':
        return i18n.global.t('validation.email')
      case 'ipAddress':
        return i18n.global.t('validation.ip_address')
      case 'macAddress':
        return i18n.global.t('validation.mac_address')
      case 'unique':
        return i18n.global.t('validation.unique')
      case 'sameAs':
        return i18n.global.t('validation.same_as')
      case 'url':
        return i18n.global.t('validation.url')
      case 'matchHash':
        return i18n.global.t('validation.reseller_code')
      default:
        return ''
    }
  }
}
